// One card on the board.
//
// IT IS DRAGGABLE AND IT IS ALSO A PAIR OF BUTTONS, and the second half is not a
// fallback. A board that can only be rearranged by dragging cannot be used by a
// keyboard, by a screen reader, by a switch or by anyone whose hands do not do
// precise gestures - and drag-and-drop has no accessible native equivalent, so
// the buttons are the interface and the drag is the shortcut. They make exactly
// the same moves through exactly the same call.
//
// Each button is NAMED FOR THE CARD AND THE COLUMN it acts on. A board is a page
// of controls whose visible glyph is the same one over and over, and a list that
// reads "Move right, Move right, Move right" says which button exists and not
// which card it moves - the same reason Settings names its Restore buttons.

import type { BoardCard, BoardColumn, IsoDay } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { cardMeta, isOverdue, moveLabel } from './board-model.ts';

interface Props {
  card: BoardCard;
  /** The column it is in, which is what its two arrows are measured from. */
  column: BoardColumn;
  left: BoardColumn | null;
  right: BoardColumn | null;
  today: IsoDay;
  /** A write against this card is in flight, so nothing on it may be pressed. */
  busy: boolean;
  dragging: boolean;
  onMove: (toKey: string) => void;
  onArchive: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

export function BoardCardView({
  card,
  column,
  left,
  right,
  today,
  busy,
  dragging,
  onMove,
  onArchive,
  onDragStart,
  onDragEnd,
}: Props) {
  const meta = cardMeta(card, today);

  return (
    <li
      className={['bcard', column.completes ? 'done' : '', dragging ? 'dragging' : '']
        .filter(Boolean)
        .join(' ')}
      // A card with a write in flight is not draggable: the drop would queue a
      // second move behind the first and the two would land in either order.
      draggable={!busy}
      onDragStart={(event) => {
        // `text/plain` because it is the one type every browser carries, and the
        // id because a drop reads it back rather than trusting a variable that a
        // second drag would have overwritten.
        event.dataTransfer.setData('text/plain', card.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      <div className="bcard-txt">
        <p className="bcard-title">{card.title}</p>
        {(meta !== '' || card.hasNote) && (
          <p className={isOverdue(card, today) ? 'bcard-meta late' : 'bcard-meta'}>
            {meta}
            {/* Drawn from `hasNote`, never from the prose: the server derives
                the one, so a card cannot show a glyph the API did not send. */}
            {card.hasNote && (
              <span className="bcard-note" title="Has a note">
                <Icon name="note" />
                <span className="sr">Has a note</span>
              </span>
            )}
          </p>
        )}
      </div>

      <div className="bcard-acts">
        <button
          className="iconbtn"
          aria-label={
            left === null ? `"${card.title}" is in the first column` : moveLabel(card.title, left)
          }
          disabled={left === null || busy}
          onClick={() => left !== null && onMove(left.key)}
        >
          <Icon name="chevL" />
        </button>
        <button
          className="iconbtn"
          aria-label={
            right === null ? `"${card.title}" is in the last column` : moveLabel(card.title, right)
          }
          disabled={right === null || busy}
          onClick={() => right !== null && onMove(right.key)}
        >
          <Icon name="chevR" />
        </button>
        {/* Archive, never delete: it is the destructive action that can be
            undone, which is why it is the one this screen offers (AGENTS.md).
            The Undo is on the toast the shell draws. */}
        <button
          className="iconbtn"
          aria-label={`Archive "${card.title}"`}
          disabled={busy}
          onClick={onArchive}
        >
          <Icon name="archive" />
        </button>
      </div>
    </li>
  );
}
